import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../config/supabase';
import { 
  ReloadIcon, 
  FunnelIcon, 
  ChevronDownIcon, 
  ChevronUpIcon, 
  CompassIcon, 
  LoadingIcon, 
  AccessibleIcon, 
  WeatherIcon, 
  EuroIcon,
  CultureIcon,
  ArtIcon,
  MusicIcon,
  TheaterIcon,
  CinemaIcon,
  FestivalIcon,
  SportIcon,
  WorkshopIcon,
  MarketIcon,
  ConferenceIcon,
  NatureIcon,
  FamilyIcon
} from './icons/NavIcons';
import { anonymizePosition } from '../utils/geolocation';

const FilterPanel = ({
  showFilters,
  setShowFilters,
  filterDate,
  setFilterDate,
  filterEndDate,
  setFilterEndDate,
  filterLocation,
  setFilterLocation,
  communeName,
  setCommuneName,
  communeInseeCode,
  setCommuneInseeCode,
  searchPosition,
  setSearchPosition,
  filterDistance,
  setFilterDistance,
  nearbyPostalCodes,
  setNearbyPostalCodes,
  setNearbyCommuneInsee,
  filterCategories,
  setFilterCategories,
  categories,
  filterAgeGroup,
  setFilterAgeGroup,
  ageGroups,
  filterAllWeather,
  setFilterAllWeather,
  filterAccessible,
  setFilterAccessible,
  filterFree,
  setFilterFree,
  onApply,
  onLocalGPSPositionUpdate,
  onLocationChange,
  onLocationLoadingChange,
  eventsCount = 0
}) => {
  const { t, i18n } = useTranslation();
  const geolocationAttemptedRef = useRef(false);
  
  const [searchInput, setSearchInput] = useState('');
  const [locationLoading, setLocationLoading] = useState(false);
  const [debouncedFilterDistance, setDebouncedFilterDistance] = useState(filterDistance);
  
  // Synchroniser locationLoading avec HomePage
  useEffect(() => {
    if (onLocationLoadingChange) {
      onLocationLoadingChange(locationLoading);
    }
  }, [locationLoading, onLocationLoadingChange]);
  
  const [showPostalCodesList, setShowPostalCodesList] = useState(false);
  const [hasAttemptedGeolocation, setHasAttemptedGeolocation] = useState(false);
  const [communeSuggestions, setCommuneSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [showCategoriesDropdown, setShowCategoriesDropdown] = useState(false);

  // États pour la gestion des positions
  const [localGPSPosition, setLocalGPSPosition] = useState(null); // Position GPS actuelle (READ-ONLY, non anonymisée, non sauvegardée)
  const [searchPositionState, setSearchPositionState] = useState(null); // Position de la commune recherchée (sauvegardée)
  const [lastUserPosition, setLastUserPosition] = useState(null); // Position GPS anonymisée (sauvegardée)
  const [lastSearchTime, setLastSearchTime] = useState(null); // Timestamp de la dernière recherche
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);
  const [isInitialGeolocationDone, setIsInitialGeolocationDone] = useState(false); // Flag CAS 1 : géolocalisation initiale

  // Nouvel état pour la période sélectionnée
  const [selectedPeriod, setSelectedPeriod] = useState(() => 
    localStorage.getItem('selectedPeriod') || '7days'
  );

  // Charger les positions et le cache des codes postaux depuis localStorage
  useEffect(() => {
    const savedLastUserPosition = localStorage.getItem('lastUserPosition');
    const savedSearchPosition = localStorage.getItem('searchPosition');
    const savedTime = localStorage.getItem('lastSearchTime');
    
    // Charger le cache des codes postaux
    const cachedPostalCodes = localStorage.getItem('cachedNearbyPostalCodes');
    const cachedCommuneInsee = localStorage.getItem('cachedNearbyCommuneInsee');
    const cachedSearchPosition = localStorage.getItem('cachedSearchPosition');
    const cachedFilterDistance = localStorage.getItem('cachedFilterDistance');
    
    if (cachedPostalCodes && cachedCommuneInsee && cachedSearchPosition && cachedFilterDistance) {
      try {
        const parsedPostalCodes = JSON.parse(cachedPostalCodes);
        const parsedCommuneInsee = JSON.parse(cachedCommuneInsee);
        const parsedCachedPosition = JSON.parse(cachedSearchPosition);
        const parsedDistance = parseFloat(cachedFilterDistance);
        
        // Vérifier que les paramètres correspondent
        if (searchPosition && 
            parsedCachedPosition.latitude === searchPosition.latitude &&
            parsedCachedPosition.longitude === searchPosition.longitude &&
            parsedDistance === filterDistance) {
          setNearbyPostalCodes(parsedPostalCodes);
          setNearbyCommuneInsee(parsedCommuneInsee);
          console.log('[Cache] Codes postaux chargés depuis le cache:', parsedPostalCodes.length);
        }
      } catch (e) {
        console.error('[Cache] Erreur lors du chargement du cache:', e);
      }
    }
    
    if (savedLastUserPosition) {
      try {
        const position = JSON.parse(savedLastUserPosition);
        setLastUserPosition(position);
        console.log('[Storage] lastUserPosition chargée:', position);
      } catch (e) {
        console.error('[Storage] Erreur lors du chargement de lastUserPosition:', e);
      }
    }
    
    if (savedSearchPosition) {
      try {
        const position = JSON.parse(savedSearchPosition);
        setSearchPositionState(position);
        console.log('[Storage] searchPosition chargée:', position);
      } catch (e) {
        console.error('[Storage] Erreur lors du chargement de searchPosition:', e);
      }
    }
    
    if (savedTime) {
      setLastSearchTime(Number(savedTime));
    }
    
    // Vérifier si la géolocalisation initiale a déjà été faite
    const initialGeolocationFlag = localStorage.getItem('isInitialGeolocationDone');
    if (initialGeolocationFlag === 'true') {
      setIsInitialGeolocationDone(true);
    }

    // Charger les dates sauvegardées si période custom
    const savedPeriod = localStorage.getItem('selectedPeriod');
    const savedStartDate = localStorage.getItem('filterDate');
    const savedEndDate = localStorage.getItem('filterEndDate');
    
    if (savedPeriod === 'custom' && savedStartDate && savedEndDate) {
      setFilterDate(savedStartDate);
      setFilterEndDate(savedEndDate);
      console.log('[Storage] Dates personnalisées chargées:', { savedStartDate, savedEndDate });
    } else if (savedPeriod && savedPeriod !== 'custom') {
      // La période est déjà chargée dans le useState
      updateDatesFromPeriod(savedPeriod);
    }
  }, []);

  // Debounce du filtre de distance
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilterDistance(filterDistance);
    }, 500);
    return () => clearTimeout(timer);
  }, [filterDistance]);

  // Fonction pour récupérer la ville par défaut depuis la base de données
  const fetchDefaultCity = async () => {
    // Important : garder locationLoading actif pendant la récupération
    setLocationLoading(true);
    try {
      const { data, error } = await supabase
        .from('parameters')
        .select('value')
        .eq('parameter', 'city')
        .single();
      
      if (!error && data && data.value) {
        console.log('[DefaultCity] Paramètre city trouvé:', data.value);
        
        // Rechercher la commune correspondante
        const { data: communeData, error: communeError } = await supabase
          .from('communes_light')
          .select('latitude, longitude, nom_de_la_commune, code_postal, code_commune_insee')
          .or(`code_postal.eq.${data.value},nom_de_la_commune.ilike.%${data.value}%`)
          .limit(1)
          .single();
        
        if (!communeError && communeData) {
          const communePos = {
            latitude: parseFloat(communeData.latitude),
            longitude: parseFloat(communeData.longitude)
          };
          
          setSearchPosition(communePos);
          localStorage.setItem('searchPosition', JSON.stringify(communePos));
          setFilterLocation(communeData.code_postal);
          setCommuneName(communeData.nom_de_la_commune || '');
          setCommuneInseeCode(communeData.code_commune_insee || '');
          console.log('[DefaultCity] Ville par défaut définie:', communeData.nom_de_la_commune, communeData.code_postal);
          
          // Marquer la géolocalisation initiale comme terminée
          setIsInitialGeolocationDone(true);
          localStorage.setItem('isInitialGeolocationDone', 'true');
          
          setLocationLoading(false);
          return true; // Succès
        } else {
          console.warn('[DefaultCity] Commune introuvable pour:', data.value);
        }
      } else {
        console.log('[DefaultCity] Aucun paramètre city trouvé ou erreur:', error);
      }
    } catch (error) {
      console.error('[DefaultCity] Erreur lors de la récupération du paramètre city:', error);
    }
    setLocationLoading(false);
    return false; // Échec
  };

  // Fonction utilitaire pour obtenir la position GPS de l'utilisateur
  // RÈGLES : localGPSPosition est READ-ONLY
  // Met à jour filterLocation/searchPosition UNIQUEMENT dans :
  //   - CAS 1 : Géolocalisation initiale (premier chargement sans position sauvegardée)
  //   - CAS 2 : Reset complet après 30min d'inactivité (via handleCompleteReset)
  const resolveUserLocation = async (options = { fallbackAlert: false, updateSearchLocation: false, isInitialGeolocOrReset: false, onError: null }) => {
    setLocationLoading(true);

    if (!navigator.geolocation) {
      console.log('[GeoLoc] navigator.geolocation indisponible');
      if (options.fallbackAlert) {
        alert(t('filters.geolocationUnavailable'));
      }
      setLocationLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        const gpsPos = { latitude, longitude };
        setLocalGPSPosition(gpsPos);
        
        // Appeler le callback pour mettre à jour HomePage
        if (onLocalGPSPositionUpdate) {
          onLocalGPSPositionUpdate(gpsPos);
        }
        
        // Sauvegarder la position anonymisée
        const anonymized = anonymizePosition(latitude, longitude);
        const anonymizedPos = { latitude: anonymized.lat, longitude: anonymized.lng };
        setLastUserPosition(anonymizedPos);
        localStorage.setItem('lastUserPosition', JSON.stringify(anonymizedPos));
        console.log('[GeoLoc] Position GPS mise à jour (GPS):', gpsPos, 'anonymisée:', anonymizedPos);

        // RÈGLE : Ne mettre à jour searchPosition que dans les CAS 1 et CAS 2
        // CAS 1 : Géolocalisation initiale (!isInitialGeolocationDone)
        // CAS 2 : Reset complet après 30min (isInitialGeolocOrReset = true)
        if (!options.updateSearchLocation || !options.isInitialGeolocOrReset) {
          setLocationLoading(false);
          return;
        }

        // Chercher d'abord via polygones (RPC retourne désormais `code_commune_insee`)
        try {
          const insee = await findPostalCodeFromPolygons(latitude, longitude);
          if (insee) {
            const { data: communeData, error: communeError } = await supabase
              .from('communes_light')
              .select('latitude, longitude, nom_de_la_commune, code_postal, code_commune_insee')
              .eq('code_commune_insee', insee)
              .limit(1)
              .single();

            if (!communeError && communeData) {
              const commune = communeData;

              // Vérifier si le code postal affiché est identique pour éviter les boucles
              if (commune.code_postal === filterLocation) {
                console.log('[GeoLoc] Code postal identique, pas de mise à jour');
                setLocationLoading(false);
                return;
              }
              
              const communePos = {
                latitude: parseFloat(commune.latitude),
                longitude: parseFloat(commune.longitude)
              };
              
              // Vérifier si la position a suffisamment changé
              if (!shouldUpdateSearchPosition(communePos)) {
                console.log('[GeoLoc] Position trop proche, pas de mise à jour');
                setLocationLoading(false);
                return;
              }
              
              setSearchPosition(communePos);
              localStorage.setItem('searchPosition', JSON.stringify(communePos));
              setFilterLocation(commune.code_postal);
              setCommuneName(commune.nom_de_la_commune || '');
              setCommuneInseeCode(commune.code_commune_insee || '');
              setSearchInput('');
              console.log('[GeoLoc] Méthode: polygones communes -> succès');
              setLocationLoading(false);
              
              // Marquer la géolocalisation initiale comme terminée (CAS 1)
              if (!isInitialGeolocationDone) {
                setIsInitialGeolocationDone(true);
                localStorage.setItem('isInitialGeolocationDone', 'true');
                console.log('[GeoLoc] CAS 1 : Géolocalisation initiale terminée');
              }
              
              // NE PAS notifier HomePage lors de la géolocalisation initiale
              // pour préserver mapCenter/mapZoom sauvegardés manuellement
              
              return;
            } else {
              console.warn('[GeoLoc] Code postal trouvé via polygones mais commune introuvable, fallback Nominatim');
            }
          } else {
            console.log('[GeoLoc] Aucun CP via polygones, fallback Nominatim');
          }
        } catch (err) {
          console.error('[GeoLoc] Erreur point-in-polygon, fallback Nominatim:', err);
        }

        // Fallback Nominatim
        try {
          const nominatimUrl = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`;
          const response = await fetch(nominatimUrl, {
            headers: {
              'Accept-Language': i18n.language,
              'User-Agent': 'FamilyEventsApp'
            }
          });
          const data = await response.json();
          if (data.address) {
            const postalCode = data.address.postcode || '';
            
            if (postalCode) {
              // Récupérer les coordonnées de la commune depuis la base de données
              const { data: communeData, error: communeError } = await supabase
                .from('communes_light')
                .select('latitude, longitude, nom_de_la_commune, code_postal, code_commune_insee')
                .eq('code_postal', postalCode)
                .limit(1)
                .single();
              
              if (!communeError && communeData) {
                // Vérifier si le code postal est différent pour éviter les boucles
                if (communeData.code_postal === filterLocation) {
                  console.log('[GeoLoc] Code postal identique, pas de mise à jour');
                  setLocationLoading(false);
                  return;
                }
                
                const communePos = {
                  latitude: parseFloat(communeData.latitude),
                  longitude: parseFloat(communeData.longitude)
                };
                
                // Vérifier si la position a suffisamment changé
                if (!shouldUpdateSearchPosition(communePos)) {
                  console.log('[GeoLoc] Position trop proche, pas de mise à jour');
                  setLocationLoading(false);
                  return;
                }
                
                setSearchPosition(communePos);
                localStorage.setItem('searchPosition', JSON.stringify(communePos));
                setFilterLocation(communeData.code_postal);
                setCommuneName(communeData.nom_de_la_commune);
                setCommuneInseeCode(communeData.code_commune_insee || '');
                setSearchInput('');
                console.log('[GeoLoc] Méthode: Nominatim -> succès');
                
                // Marquer la géolocalisation initiale comme terminée (CAS 1)
                if (!isInitialGeolocationDone) {
                  setIsInitialGeolocationDone(true);
                  localStorage.setItem('isInitialGeolocationDone', 'true');
                  console.log('[GeoLoc] CAS 1 : Géolocalisation initiale terminée');
                }
                
                // NE PAS notifier HomePage lors de la géolocalisation initiale
                // pour préserver mapCenter/mapZoom sauvegardés manuellement
              }
            }
          } else {
            console.warn('[GeoLoc] Nominatim: adresse manquante');
          }
        } catch (error) {
          console.error('[Geocoding] Erreur Nominatim:', error);
        } finally {
          setLocationLoading(false);
        }
      },
      (error) => {
        console.warn('[GeoLoc] Échec GPS:', error.code, error.message);
        
        // Appeler le callback onError si fourni
        if (options.onError) {
          options.onError(error);
          // Ne pas réinitialiser locationLoading ici si onError est défini
          // car le callback va gérer le fallback (fetchDefaultCity)
        } else {
          setLocationLoading(false);
        }
        
        if (options.fallbackAlert) {
          if (error.code === 1) {
            // Permission refusée
            alert(t('filters.geolocationDenied'));
          } else {
            alert(t('filters.geolocationError'));
          }
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 8000,
        maximumAge: 0
      }
    );
  };

  // Géolocalisation automatique au chargement (CAS 1 : géolocalisation initiale)
  useEffect(() => {
    const getUserLocation = async () => {
      if (geolocationAttemptedRef.current || hasAttemptedGeolocation) return;
      setHasAttemptedGeolocation(true);
      geolocationAttemptedRef.current = true;
      
      // CAS 1 : Si aucune localisation de recherche n'est définie, c'est la géolocalisation initiale
      if (!searchPosition && !filterLocation && !isInitialGeolocationDone) {
        console.log('[GeoLoc] CAS 1 : Géolocalisation initiale');
        
        // Tenter d'abord la géolocalisation avec callback d'erreur
        let geolocationSuccess = false;
        
        await resolveUserLocation({ 
          updateSearchLocation: true, 
          isInitialGeolocOrReset: true,
          onError: async (error) => {
            // Si l'utilisateur refuse la géolocalisation ou erreur
            console.log('[GeoLoc] Échec de géolocalisation, tentative de ville par défaut');
            
            // Récupérer et définir la ville par défaut
            const defaultCitySet = await fetchDefaultCity();
            
            if (defaultCitySet) {
              console.log('[GeoLoc] Ville par défaut définie avec succès');
            } else {
              console.warn('[GeoLoc] Impossible de définir la ville par défaut');
            }
          }
        });
      } else {
        // Obtenir la position GPS sans mettre à jour la recherche (uniquement marqueur bleu)
        await resolveUserLocation({ updateSearchLocation: false, isInitialGeolocOrReset: false });
      }
    };
    getUserLocation();
    // eslint-disable-next-line
  }, []);

  // Recherche de codes postaux à proximité avec cache intelligent
  useEffect(() => {
    const fetchNearbyPostalCodes = async () => {
      if (!searchPosition) {
        return;
      }
      
      // Vérifier si on a des données en cache valides
      const cachedPostalCodes = localStorage.getItem('cachedNearbyPostalCodes');
      const cachedCommuneInsee = localStorage.getItem('cachedNearbyCommuneInsee');
      const cachedSearchPosition = localStorage.getItem('cachedSearchPosition');
      const cachedFilterDistance = localStorage.getItem('cachedFilterDistance');
      
      if (cachedPostalCodes && cachedCommuneInsee && cachedSearchPosition && cachedFilterDistance) {
        try {
          const parsedCachedPosition = JSON.parse(cachedSearchPosition);
          const parsedDistance = parseFloat(cachedFilterDistance);
          
          // Si les paramètres n'ont pas changé, utiliser le cache
          if (parsedCachedPosition.latitude === searchPosition.latitude &&
              parsedCachedPosition.longitude === searchPosition.longitude &&
              parsedDistance === debouncedFilterDistance) {
            console.log('[Cache] Utilisation du cache existant pour les codes postaux');
            return;
          }
        } catch (e) {
          console.error('[Cache] Erreur lors de la vérification du cache:', e);
        }
      }
      
      // Si pas de cache valide, faire l'appel API
      console.log('[API] Appel à get_nearby_postal_codes');
      
      if (debouncedFilterDistance === 0 && communeInseeCode) {
        const { data, error } = await supabase
          .from('communes_light')
          .select('code_postal, nom_de_la_commune, code_commune_insee')
          .eq('code_commune_insee', communeInseeCode)
          .limit(1);
        
        if (error) return;
        
        if (data && data.length > 0) {
          setNearbyPostalCodes(data);
          setNearbyCommuneInsee(data.map(d => d.code_commune_insee));
          
          // Sauvegarder dans le cache
          localStorage.setItem('cachedNearbyPostalCodes', JSON.stringify(data));
          localStorage.setItem('cachedNearbyCommuneInsee', JSON.stringify(data.map(d => d.code_commune_insee)));
          localStorage.setItem('cachedSearchPosition', JSON.stringify(searchPosition));
          localStorage.setItem('cachedFilterDistance', debouncedFilterDistance.toString());
        }
        return;
      }

      const distanceInMeters = debouncedFilterDistance * 1000;

      try {
        const { data, error } = await supabase.rpc('get_nearby_postal_codes', {
          user_lat: searchPosition.latitude,
          user_lon: searchPosition.longitude,
          distance_meters: distanceInMeters
        });

        if (error) return;
        
        const uniquePostalCodes = data?.reduce((acc, curr) => {
          if (!acc.find(item => item.code_postal === curr.code_postal)) {
            acc.push({
              code_postal: curr.code_postal,
              nom_de_la_commune: curr.nom_de_la_commune,
              code_commune_insee: curr.code_commune_insee
            });
          }
          return acc;
        }, []) || [];

        setNearbyPostalCodes(uniquePostalCodes);
        const communeInseeArray = uniquePostalCodes.map(d => d.code_commune_insee);
        setNearbyCommuneInsee(communeInseeArray);
        
        // Sauvegarder dans le cache
        localStorage.setItem('cachedNearbyPostalCodes', JSON.stringify(uniquePostalCodes));
        localStorage.setItem('cachedNearbyCommuneInsee', JSON.stringify(communeInseeArray));
        localStorage.setItem('cachedSearchPosition', JSON.stringify(searchPosition));
        localStorage.setItem('cachedFilterDistance', debouncedFilterDistance.toString());
        
        console.log('[Cache] Données sauvegardées dans le cache');
      } catch (error) {
        console.error('[PostalCodes] Erreur:', error);
      }
    };

    fetchNearbyPostalCodes();
  }, [searchPosition, debouncedFilterDistance, communeInseeCode]);

  // Recherche de communes
  useEffect(() => {
    if (!searchInput.trim() || (filterLocation && communeName)) {
      setCommuneSuggestions([]);
      setShowSuggestions(false);
      setSearchError('');
      return;
    }

    const timer = setTimeout(async () => {
      const input = searchInput.trim();
      
      try {
        const isPostalCode = /^\d{5}$/.test(input);
        
        if (isPostalCode) {
          const { data, error } = await supabase
            .from('communes_light')
            .select('latitude, longitude, nom_de_la_commune, code_postal, code_commune_insee')
            .eq('code_postal', input)
            .limit(50);

          if (error || !data || data.length === 0) {
            setSearchError(t('filters.noResults'));
            setTimeout(() => setSearchError(''), 3000);
            return;
          }
          
          if (data.length === 1) {
            const commune = data[0];
            setCommuneName(commune.nom_de_la_commune);
            setFilterLocation(commune.code_postal);
            setCommuneInseeCode(commune.code_commune_insee);
            setSearchInput('');
            setShowSuggestions(false);
            setCommuneSuggestions([]);
            setSearchError('');
            const pos = { 
              latitude: parseFloat(commune.latitude), 
              longitude: parseFloat(commune.longitude) 
            };
            setSearchPosition(pos);
            localStorage.setItem('searchPosition', JSON.stringify(pos));
          } else {
            setCommuneSuggestions(data);
            setShowSuggestions(true);
            setSearchError('');
          }
        } else {
          const { data, error } = await supabase
            .from('communes_light')
            .select('latitude, longitude, nom_de_la_commune, code_postal, code_commune_insee')
            .or(`nom_de_la_commune.ilike.%${input}%,code_postal.like.${input}%`)
            .limit(50);

          if (error || !data || data.length === 0) {
            setSearchError(t('filters.noResults'));
            setTimeout(() => setSearchError(''), 3000);
            return;
          }
          
          if (data.length === 1) {
            const commune = data[0];
            setFilterLocation(commune.code_postal);
            setCommuneName(commune.nom_de_la_commune);
            setCommuneInseeCode(commune.code_commune_insee);
            setSearchInput('');
            setShowSuggestions(false);
            setCommuneSuggestions([]);
            setSearchError('');
            const pos = { 
              latitude: parseFloat(commune.latitude), 
              longitude: parseFloat(commune.longitude) 
            };
            setSearchPosition(pos);
            localStorage.setItem('searchPosition', JSON.stringify(pos));
          } else {
            setCommuneSuggestions(data);
            setShowSuggestions(true);
            setSearchError('');
          }
        }
      } catch (error) {
        setSearchError(t('filters.noResults'));
        setTimeout(() => setSearchError(''), 3000);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [searchInput, filterLocation, communeName, t]);

  const handleSelectCommune = async (commune) => {
    const { data, error } = await supabase
      .from('communes_light')
      .select('latitude, longitude, nom_de_la_commune, code_postal, code_commune_insee')
      .eq('code_commune_insee', commune.code_commune_insee)
      .limit(1)
      .single();
    
    if (error || !data) {
      setSearchError(t('filters.noResults'));
      setTimeout(() => setSearchError(''), 3000);
      return;
    }
    
    // Vérifier si c'est le même lieu ET que searchPosition existe déjà
    if (data.code_postal === filterLocation && searchPosition) {
      console.log('[FilterPanel] Code postal identique avec position existante, pas de mise à jour');
      setSearchInput('');
      setShowSuggestions(false);
      return;
    }
    
    console.log('[FilterPanel] Sélection commune:', {
      oldLocation: filterLocation,
      newLocation: data.code_postal,
      commune: data.nom_de_la_commune,
      hasSearchPosition: !!searchPosition
    });
    
    setFilterLocation(data.code_postal);
    setCommuneName(data.nom_de_la_commune);
    setCommuneInseeCode(data.code_commune_insee);
    setSearchInput('');
    setShowSuggestions(false);
    setCommuneSuggestions([]);
    setSearchError('');
    const pos = { 
      latitude: parseFloat(data.latitude), 
      longitude: parseFloat(data.longitude) 
    };
    setSearchPosition(pos);
    localStorage.setItem('searchPosition', JSON.stringify(pos));
    
    // Notifier HomePage pour recentrer la carte (action manuelle utilisateur)
    if (onLocationChange) {
      onLocationChange();
    }
  };

  const handleClearLocation = () => {
    setFilterLocation('');
    setCommuneName('');
    setCommuneInseeCode('');
    setSearchInput('');
    setSearchPosition(null);
    setNearbyPostalCodes([]);
    setNearbyCommuneInsee([]);
    setCommuneSuggestions([]);
    setShowSuggestions(false);
    setSearchError('');
    localStorage.removeItem('filterLocation');
    localStorage.removeItem('communeName');
    localStorage.removeItem('communeInseeCode');
    localStorage.removeItem('searchPosition');
    // Nettoyer le cache des codes postaux
    localStorage.removeItem('cachedNearbyPostalCodes');
    localStorage.removeItem('cachedNearbyCommuneInsee');
    localStorage.removeItem('cachedSearchPosition');
    localStorage.removeItem('cachedFilterDistance');
  };

  const handleResetToCurrentLocation = async () => {
    // Action manuelle : mettre à jour la localisation de recherche avec la position GPS actuelle
    // Ce n'est PAS un CAS 1 (déjà fait) ni CAS 2 (reset 30min), c'est une action volontaire
    await resolveUserLocation({ fallbackAlert: true, updateSearchLocation: true, isInitialGeolocOrReset: true });
    
    // Notifier HomePage pour recentrer la carte (action manuelle utilisateur)
    if (onLocationChange) {
      onLocationChange();
    }
  };

  const handleResetDates = () => {
    setSelectedPeriod('7days');
    updateDatesFromPeriod('7days');
    localStorage.setItem('selectedPeriod', '7days');
  };

  const handleResetCategories = () => {
    setFilterCategories([]);
  };

  const toggleCategory = (categoryId) => {
    setFilterCategories(prev => 
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  // Nouvelle version: recherche du code postal via géométrie (ST_Contains / ST_Intersects)
  // Note: la RPC `check_point_in_commune` retourne maintenant `code_commune_insee` (TEXT) ou null
  const findPostalCodeFromPolygons = async (latitude, longitude) => {
    try {
      console.log('[GeoLoc] Tentative PostGIS check_point_in_commune (retourne INSEE)');

      const { data, error } = await supabase.rpc('check_point_in_commune', {
        p_lat: latitude,
        p_lon: longitude
      });

      if (error) {
        console.error('[GeoLoc] Erreur RPC check_point_in_commune:', error?.message || error);
        return null;
      }

      // La fonction retourne directement le `code_commune_insee` (TEXT) ou null
      if (data) {
        console.log(`[GeoLoc] Match PostGIS (INSEE): ${data}`);
        return data;
      }

      console.log('[GeoLoc] Aucun match PostGIS');
      return null;
    } catch (e) {
      console.error('[GeoLoc] Exception PostGIS:', e);
      return null;
    }
  };

  // Nouvelle fonction pour mettre à jour les dates selon la période
  const updateDatesFromPeriod = (period) => {
    const today = new Date();
    const startDate = today.toISOString().slice(0, 10);
    let endDate;

    switch (period) {
      case 'today':
        endDate = startDate;
        break;
      case '3days':
        const date3 = new Date(today);
        date3.setDate(date3.getDate() + 3);
        endDate = date3.toISOString().slice(0, 10);
        break;
      case '7days':
        const date7 = new Date(today);
        date7.setDate(date7.getDate() + 7);
        endDate = date7.toISOString().slice(0, 10);
        break;
      case '14days':
        const date14 = new Date(today);
        date14.setDate(date14.getDate() + 14);
        endDate = date14.toISOString().slice(0, 10);
        break;
      case 'custom':
        // Ne pas modifier les dates en mode personnalisé
        return;
      default:
        const dateDefault = new Date(today);
        dateDefault.setDate(dateDefault.getDate() + 7);
        endDate = dateDefault.toISOString().slice(0, 10);
    }

    setFilterDate(startDate);
    setFilterEndDate(endDate);
    
    // Sauvegarder les dates calculées
    localStorage.setItem('filterDate', startDate);
    localStorage.setItem('filterEndDate', endDate);
  };

  // Gérer le changement de période
  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    localStorage.setItem('selectedPeriod', period);
    
    if (period !== 'custom') {
      updateDatesFromPeriod(period);
    }
  };

  // Calculer le nombre de filtres actifs
  useEffect(() => {
    let count = 0;
    if (filterCategories && filterCategories.length > 0) count++;
    if (filterAgeGroup) count++;
    if (filterAllWeather) count++;
    if (filterAccessible) count++;
    if (filterFree) count++;
    
    // Considérer le filtre de date actif uniquement si période personnalisée
    if (selectedPeriod === 'custom') count++;
    
    setActiveFiltersCount(count);
  }, [filterCategories, filterAgeGroup, filterAllWeather, filterAccessible, filterFree, selectedPeriod]);

  // ⚡ RÈGLE PRIORITAIRE : timeDiffMinutes (appliquée en 1er)
  // Vérification périodique pour auto-reset après 30 minutes d'inactivité
  // Si timeDiffMinutes > 30min → déclenche handleCompleteReset() (CAS 2)
  //   - Étape 1 : Relocalise via navigator.geolocation → met à jour localGPSPosition
  //   - Étape 2 : Met à jour filterLocation selon commune trouvée
  //   - Étape 3 : Met à jour searchPosition (centroïde commune)
  //   - Étape 4 : Invalide et force le recentrage de mapCenter et mapZoom
  useEffect(() => {
    const checkAutoReset = () => {
      if (!lastSearchTime) return;

      const now = Date.now();
      const timeDiffMinutes = (now - lastSearchTime) / 1000 / 60; // en minutes

      // CAS 2 : Réinitialiser automatiquement si plus de 30 minutes se sont écoulées
      if (timeDiffMinutes > 30) {
        console.log('[AutoReset] ⚡ RÈGLE PRIORITAIRE : timeDiffMinutes =', timeDiffMinutes.toFixed(1), 'min > 30min');
        console.log('[AutoReset] CAS 2 : Déclenchement du reset complet après 30min d\'inactivité');
        handleCompleteReset();
      }
    };

    // Vérification périodique toutes les 5 minutes
    const interval = setInterval(checkAutoReset, 5 * 60 * 1000);
    
    // Vérifier immédiatement au montage
    checkAutoReset();

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastSearchTime]);

  // Fonction pour calculer la distance entre deux points (formule de Haversine)
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Rayon de la Terre en km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c; // Distance en km
  };

  // Fonction helper pour vérifier si searchPosition doit être mis à jour
  const shouldUpdateSearchPosition = (newPos) => {
    if (!searchPosition) return true;
    
    const distance = calculateDistance(
      searchPosition.latitude,
      searchPosition.longitude,
      newPos.latitude,
      newPos.longitude
    );
    
    // Ne mettre à jour que si la distance est > 0.1 km (100m)
    return distance > 0.1;
  };

  // Fonction de recherche avec mise à jour du timestamp
  const handleSearch = () => {
    if (searchPosition) {
      // La position de recherche est déjà sauvegardée, on met juste à jour le timestamp
      setLastSearchTime(Date.now());
      localStorage.setItem('lastSearchTime', Date.now().toString());
      console.log('[Search] Temps de recherche sauvegardé:', searchPosition);
    }
    setShowFilters(false);
    if (onApply) onApply();
  };

  // Réinitialisation complète (CAS 2 : reset après 30min d'inactivité OU action manuelle)
  const handleCompleteReset = async () => {
    console.log('[Reset] CAS 2 : Réinitialisation complète des filtres');
    
    // Réinitialiser les dates à 7 jours
    setSelectedPeriod('7days');
    updateDatesFromPeriod('7days');
    localStorage.setItem('selectedPeriod', '7days');
    
    // Réinitialiser les catégories et filtres
    setFilterCategories([]);
    setFilterAgeGroup('');
    setFilterAllWeather(false);
    setFilterAccessible(false);
    setFilterFree(false);
    
    // Réinitialiser la distance
    setFilterDistance(3);
    
    // CAS 2 : Étapes 1-3
    // Étape 1 : Relocalise l'utilisateur via navigator.geolocation → met à jour localGPSPosition
    // Étape 2 : Met à jour filterLocation selon la commune trouvée
    // Étape 3 : Met à jour searchPosition (centroïde de la commune)
    console.log('[Reset] CAS 2 : Relocalisation utilisateur (étapes 1-3)');
    await resolveUserLocation({ fallbackAlert: false, updateSearchLocation: true, isInitialGeolocOrReset: true });
    
    // Réinitialiser la recherche
    setSearchInput('');
    setLastSearchTime(null);
    
    // Nettoyer le localStorage (mais garder lastUserPosition)
    localStorage.removeItem('lastSearchTime');
    // Nettoyer le cache des codes postaux
    localStorage.removeItem('cachedNearbyPostalCodes');
    localStorage.removeItem('cachedNearbyCommuneInsee');
    localStorage.removeItem('cachedSearchPosition');
    localStorage.removeItem('cachedFilterDistance');
    
    // Étape 4 : Invalide mapCenter et mapZoom sauvegardés (notifier HomePage)
    // HomePage va détecter le changement de searchPosition et recentrer automatiquement
    console.log('[Reset] CAS 2 : Invalidation mapCenter/mapZoom (étape 4)');
    localStorage.removeItem('mapCenter');
    localStorage.removeItem('mapZoom');
    
    console.log('[Reset] CAS 2 : Réinitialisation terminée');
    
    // Notifier HomePage pour recentrage complet
    if (onLocationChange) {
      onLocationChange();
    }
    
    // Appliquer les filtres pour mettre à jour HomePage et EventList
    if (onApply) {
      onApply();
    }
  };

  // Mapping des catégories vers les icônes SVG
  const categoryIcons = {
    'culture': CultureIcon,
    'art': ArtIcon,
    'music': MusicIcon,
    'theater': TheaterIcon,
    'cinema': CinemaIcon,
    'festival': FestivalIcon,
    'sport': SportIcon,
    'atelier': WorkshopIcon,
    'workshop': WorkshopIcon,
    'market': MarketIcon,
    'conference': ConferenceIcon,
    'nature': NatureIcon,
    'family': FamilyIcon
  };

  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-[999] bg-white border-b border-gray-200 shadow-sm pt-4 px-4 pb-4 transition-all duration-300">
        <button 
          onClick={() => setShowFilters(!showFilters)}
          className="w-full py-2 px-4 bg-gray-100 rounded-lg flex items-center justify-between hover:bg-gray-200 transition-colors"
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <FunnelIcon className="w-5 h-5 flex-shrink-0 stroke-[2] text-[rgb(40,148,216)]"/>
              {/* <span className="font-medium">{t('eventList.filters')}</span> */}
              {activeFiltersCount > 0 && (
                <span className="bg-[#2894D8] text-white text-xs px-2 py-0.5 rounded-full font-semibold">
                  {activeFiltersCount}
                </span>
              )}
              {eventsCount >= 200 && (
                <svg className="w-5 h-5 text-bleu3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              )}
            </div>
            {(filterLocation || communeName) && (
              <>
                <span className="text-gray-400 font-medium">|</span>
                <div className="flex items-center gap-1 text-xs text-gray-600 truncate font-medium">
                  <span className="truncate">
                    {filterLocation && communeName 
                      ? `${filterLocation} - ${communeName}` 
                      : filterLocation || communeName}
                  </span>
                </div>
              </>
            )}
          </div>
          {showFilters ? (
            <ChevronUpIcon className="w-5 h-5 ml-2 flex-shrink-0 stroke-[2] text-[rgb(40,148,216)]" />
          ) : (
            <ChevronDownIcon className="w-5 h-5 ml-2 flex-shrink-0 stroke-[2] text-[rgb(40,148,216)]" />
          )}
        </button>
        {showFilters && (
          <div className="mt-0 space-y-2 bg-white p-4 rounded-lg max-h-[70vh] overflow-y-auto overflow-x-hidden">
            {/* Avertissement de limite d'événements */}
            {eventsCount >= 200 && (
              <div className="bg-bleu3/10 border border-bleu3 rounded-lg p-3 mb-4">
                <div className="flex items-start gap-2">
                  <svg className="w-5 h-5 text-bleu3 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div className="flex-1">
                    <h4 className="font-semibold text-bleu2 mb-1">
                      {t('filters.maxEventsWarning.title')}
                    </h4>
                    <p className="text-sm text-gray-700">
                      {t('filters.maxEventsWarning.message')}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Bouton de réinitialisation complète */}
            <div className="mb-4 pb-4 border-b border-gray-200">
              <button
                onClick={handleCompleteReset}
                className="w-full py-2 px-4 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center gap-2 transition-colors duration-200"
              >
                <ReloadIcon className="w-5 h-5 stroke-[2]" />
                <span className="text-sm font-semibold">{t('filters.resetAll')}</span>
              </button>
            </div>

            {/* Localisation */}
            <div className="mb-0">
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-semibold">{t('filters.location')}</label>
              </div>
              <div className="grid grid-cols-[1fr,auto] gap-2">
                {/* Colonne 1: Champ de recherche */}
                <div className="relative">
                  {filterLocation && communeName ? (
                    <div className="w-full p-2 border-2 rounded-lg bg-[#2894D8] bg-opacity-10 border-[#2894D8] flex items-center justify-between">
                      <span className="text-sm font-semibold text-gray-800">
                        {filterLocation} - {communeName}
                      </span>
                      <button
                        onClick={handleClearLocation}
                        className="text-gray-500 hover:text-gray-700 focus:outline-none font-bold"
                        aria-label={t('common.clear')}
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <input 
                      type="text" 
                      placeholder={t('filters.locationPlaceholder')}
                      className="w-full p-2 border-2 rounded-lg font-medium"
                      value={searchInput}
                      onChange={e => {
                        setSearchInput(e.target.value);
                        setSearchError('');
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Backspace' && !searchInput && filterLocation && communeName) {
                          handleClearLocation();
                        }
                      }}
                      onFocus={() => {
                        if (communeSuggestions.length > 0) {
                          setShowSuggestions(true);
                        }
                      }}
                    />
                  )}
                  {searchError && (
                    <div className="absolute left-0 right-0 top-full mt-1 p-2 bg-red-50 border-2 border-red-200 rounded-lg text-sm text-red-600 font-medium">
                      {searchError}
                    </div>
                  )}
                  {showSuggestions && communeSuggestions.length > 0 && (
                    <div className="absolute left-0 right-0 top-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto z-20">
                      {communeSuggestions.map((commune, index) => (
                        <button
                          key={index}
                          onClick={() => handleSelectCommune(commune)}
                          className="w-full text-left px-3 py-2 hover:bg-gray-100 transition-colors duration-150 border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-semibold text-sm">{commune.nom_de_la_commune}</div>
                          <div className="text-xs text-gray-500 font-medium">{commune.code_postal}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Colonne 2: Bouton de localisation */}
                <button
                  onClick={handleResetToCurrentLocation}
                  className="p-2 border-2 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center min-w-[44px]"
                  disabled={locationLoading}
                  title={t('filters.useMyLocation')}
                >
                  {locationLoading ? (
                    <LoadingIcon className="w-5 h-5 stroke-[2] text-[#2894D8]" />
                  ) : (
                    <CompassIcon className="w-5 h-5 stroke-[2] text-[#2894D8]" />
                  )}
                </button>
              </div>
              {/* Communes à proximité */}
              {searchPosition && nearbyPostalCodes.length > 0 && (
                <div className="mt-0">
                  <button
                    onClick={() => setShowPostalCodesList(!showPostalCodesList)}
                    className="text-sm text-gray-500 hover:underline flex items-center gap-1 "
                  >
                    <span>
                      {nearbyPostalCodes.length} {t('filters.postalCodesFound')}
                    </span>
                    {showPostalCodesList ? (
                      <ChevronUpIcon className="w-4 h-4 stroke-[2.5] " />
                    ) : (
                      <ChevronDownIcon className="w-4 h-4 stroke-[2.5] " />
                    )}
                  </button>
                  {showPostalCodesList && (
                    <div className="mt-2 p-3 bg-gray-50 rounded-lg border-2 border-gray-200 text-sm text-gray-700 font-medium max-h-40 overflow-y-auto">
                      {nearbyPostalCodes.map((pc) => (
                        <div key={pc.code_commune_insee || pc.code_postal}>
                          {pc.code_postal}{pc.nom_de_la_commune ? ` - ${pc.nom_de_la_commune}` : ''}
                          <br />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Distance */}
            <div className="mb-4">
              <label className="block text-sm font-semibold mb-1">
                {t('filters.distance')} : {filterDistance} km
              </label>
              <input 
                type="range" 
                min="0" 
                max="50" 
                step="1"
                className="w-full accent-[#2894D8] h-2"
                style={{
                  WebkitAppearance: 'none',
                  appearance: 'none',
                  background: 'linear-gradient(to right, #2894D8 0%, #2894D8 ' + (filterDistance * 2) + '%, #e5e7eb ' + (filterDistance * 2) + '%, #e5e7eb 100%)',
                  borderRadius: '8px',
                  outline: 'none',
                }}
                value={filterDistance}
                onChange={e => setFilterDistance(Number(e.target.value))}
                disabled={!searchPosition}
              />
              <style>{`
                input[type="range"]::-webkit-slider-thumb {
                  -webkit-appearance: none;
                  appearance: none;
                  width: 28px;
                  height: 28px;
                  background: #2894D8;
                  cursor: pointer;
                  border-radius: 50%;
                  border: 4px solid white;
                  box-shadow: 0 2px 6px rgba(0,0,0,0.2);
                }
                
                input[type="range"]::-moz-range-thumb {
                  width: 28px;
                  height: 28px;
                  background: #2894D8;
                  cursor: pointer;
                  border-radius: 50%;
                  border: 4px solid white;
                  box-shadow: 0 2px 6px rgba(0,0,0,0.2);
                }
                
                input[type="range"]:disabled::-webkit-slider-thumb {
                  background: #9ca3af;
                  cursor: not-allowed;
                }
                
                input[type="range"]:disabled::-moz-range-thumb {
                  background: #9ca3af;
                  cursor: not-allowed;
                }
              `}</style>
              <div className="flex justify-between text-xs text-gray-500 font-medium mt-0">
                <span>0 km</span>
                <span>50 km</span>
              </div>
              {!searchPosition && (
                <p className="text-xs text-gray-400 font-medium mt-1">
                  {t('filters.distanceRequiresLocation')}
                </p>
              )}
            </div>

            {/* Dates avec menu déroulant */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-semibold">{t('filters.date')}</label>
                <button
                  onClick={handleResetDates}
                  className="text-sm text-[#2894D8] hover:text-[#136E9D] focus:outline-none flex items-center gap-1 font-semibold"
                >
                  <ReloadIcon className="w-4 h-4 stroke-[2]" />
                  <span>{t('filters.reset')}</span>
                </button>
              </div>
              
              {/* Menu déroulant des périodes */}
              <select 
                className="w-full p-2 border-2 rounded-lg text-sm font-medium"
                value={selectedPeriod}
                onChange={e => handlePeriodChange(e.target.value)}
              >
                <option value="today">{t('filters.periods.today')}</option>
                <option value="3days">{t('filters.periods.next3days')}</option>
                <option value="7days">{t('filters.periods.next7days')}</option>
                <option value="14days">{t('filters.periods.next14days')}</option>
                <option value="custom">{t('filters.periods.custom')}</option>
              </select>

              {/* Affichage des dates personnalisées si besoin */}
              {selectedPeriod === 'custom' && (
                <div className="flex gap-2 mt-2">
                  <div className="flex-1">
                    <label className="block text-xs text-gray-500 font-semibold mb-1">{t('filters.startDate')}</label>
                    <input 
                      type="date" 
                      className="w-full p-2 border-2 rounded-lg text-xs font-medium"
                      value={filterDate}
                      onChange={e => {
                        setFilterDate(e.target.value);
                        localStorage.setItem('filterDate', e.target.value);
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs text-gray-500 font-semibold mb-1">{t('filters.endDate')}</label>
                    <input 
                      type="date" 
                      className="w-full p-2 border-2 rounded-lg text-xs font-medium"
                      value={filterEndDate}
                      onChange={e => {
                        setFilterEndDate(e.target.value);
                        localStorage.setItem('filterEndDate', e.target.value);
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Catégories */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-1">
                <button
                  onClick={() => setShowCategoriesDropdown(!showCategoriesDropdown)}
                  className="flex items-center gap-2 text-sm font-semibold hover:text-[#2894D8] transition-colors"
                >
                  <span>{t('filters.categories')}</span>
                  {showCategoriesDropdown ? (
                    <ChevronUpIcon className="w-4 h-4 stroke-[2.5] text-[rgb(40,148,216)]" />
                  ) : (
                    <ChevronDownIcon className="w-4 h-4 stroke-[2.5] text-[rgb(40,148,216)]" />
                  )}
                  {filterCategories && filterCategories.length > 0 && (
                    <span className="bg-[#2894D8] text-white text-xs px-2 py-0.5 rounded-full font-bold">
                      {filterCategories.length}
                    </span>
                  )}
                </button>
                <button
                  onClick={handleResetCategories}
                  className="text-sm text-[#2894D8] hover:text-[#136E9D] focus:outline-none flex items-center gap-1 font-semibold"
                >
                  <ReloadIcon className="w-4 h-4 stroke-[2]" />
                  <span>{t('filters.reset')}</span>
                </button>
              </div>
              {showCategoriesDropdown && (
                <div className="flex flex-wrap gap-2">
                  {categories.map(category => {
                    const CategoryIconComponent = categoryIcons[category.name?.toLowerCase()];
                    return (
                      <button
                        key={category.id}
                        onClick={() => toggleCategory(category.id)}
                        className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-200 flex items-center gap-2 ${
                          filterCategories && filterCategories.includes(category.id)
                            ? 'text-white shadow-md'
                            : 'bg-gray-100 hover:bg-gray-200'
                        }`}
                        style={filterCategories && filterCategories.includes(category.id) ? {
                          backgroundColor: category.color_code || '#2894D8'
                        } : {}}
                      >
                        {CategoryIconComponent ? (
                          <CategoryIconComponent className="w-4 h-4" />
                        ) : (
                          <span>{category.icon}</span>
                        )}
                        <span>{t(`categories.${category.name}`)}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Classe d'âge */}
            <div className="mb-4">
              <label className="block text-sm font-semibold mb-1">{t('filters.ageGroups')}</label>
              <select 
                className="w-full p-2 border-2 rounded-lg text-xs font-medium"
                value={filterAgeGroup}
                onChange={e => setFilterAgeGroup(e.target.value)}
              >
                <option value="">{t('filters.allAgeGroups')}</option>
                {ageGroups.map(ageGroup => (
                  <option key={ageGroup.id} value={ageGroup.id}>
                    {/* {ageGroup.icon}  */}
                    {t(`ageGroups.${ageGroup.name}`)}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtres rapides */}
            <div className="flex flex-wrap gap-2">
              <button 
                onClick={() => setFilterAllWeather(!filterAllWeather)}
                className={`px-3 py-1 rounded-full text-sm font-semibold transition-colors duration-200 flex items-center gap-1 ${
                  filterAllWeather 
                    ? 'bg-[#2894D8] text-white' 
                    : 'bg-gray-200 hover:bg-gray-300'
                }`}
              >
                <WeatherIcon className="w-4 h-4" />
                <span>{t('event.allWeather')}</span>
              </button>
              <button 
                onClick={() => setFilterAccessible(!filterAccessible)}
                className={`px-3 py-1 rounded-full text-sm font-semibold transition-colors duration-200 flex items-center gap-1 ${
                  filterAccessible 
                    ? 'bg-[#2894D8] text-white' 
                    : 'bg-gray-200 hover:bg-gray-300'
                }`}
              >
                <AccessibleIcon className="w-4 h-4" />
                <span>{t('event.accessible')}</span>
              </button>
              <button 
                onClick={() => setFilterFree(!filterFree)}
                className={`px-3 py-1 rounded-full text-sm font-semibold transition-colors duration-200 flex items-center gap-1 ${
                  filterFree 
                    ? 'bg-[#2894D8] text-white' 
                    : 'bg-gray-200 hover:bg-gray-300'
                }`}
              >
                <EuroIcon className="w-4 h-4" />
                <span>{t('event.free')}</span>
              </button>
            </div>

            {/* Bouton d'application */}
            <button 
              onClick={handleSearch}
              className="w-full bg-[#2894D8] text-white py-2 rounded-lg mt-4 font-bold hover:bg-[#136E9D] hover:shadow-md transition-all duration-200"
            >
              {t('filters.apply')}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default FilterPanel;
